import clsx from "clsx";
import { useEffect, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Outlet, useLocation } from "react-router-dom";

import {
  Badge,
  ImpersonationBar,
  Navbar,
  NavbarItem,
  ProfileItemNavbar,
  useExternalNavigationGuard,
} from "@features/ui";
import { useAuth } from "@hooks/use-auth";
import { useDoSync } from "@hooks/use-sync-state";
import { useThemeColor } from "@hooks/use-theme-color";
import { useToast } from "@hooks/use-toast";
import { useUserFavoriteCareers } from "@pages/favorites/api/use-user-favorite-careers";
import { useJourney } from "@pages/journey-map/hooks/use-journey";
import { useCareerPlans } from "@pages/pathways/api/use-career-plans";
import { ErrorPage } from "@pages/shared";
import { reportReactErrorBoundary } from "@src/observability/reporting";

import colors from "@styles/colors.module.scss";

import { AppRoutes } from "./routes";

import "./app.scss";

const themeColors = {
  profile: colors.lightBlue,
  favorites: colors.jasmine,
};

export const App = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { updateColor } = useThemeColor();
  const { currentMap, journeyMapView } = useJourney();
  const { data: favorites } = useUserFavoriteCareers();
  const { data: careerPlans } = useCareerPlans();
  const { setToast } = useToast();

  useDoSync();
  useExternalNavigationGuard();

  const routeClassName = useMemo(
    () => location.state?.className ?? "map",
    [location],
  );

  useEffect(() => {
    setToast();
  }, [location]);

  useEffect(() => {
    if (routeClassName !== "map") {
      updateColor(themeColors[routeClassName]);
    }
  }, [routeClassName]);

  return (
    <div
      className={clsx(
        "tx-app-layout",
        `page-${routeClassName}`,
        `map-${currentMap}`,
        "map-3d-enabled",
        journeyMapView && "map-view",
      )}
    >
      {user.claims.impersonatorId && <ImpersonationBar user={user} />}
      <Navbar>
        <NavbarItem
          title="Explore"
          icon="mountain"
          to={AppRoutes.journeyMap}
          state={{ className: "map" }}
        />
        <NavbarItem
          className="pathways-navbar-item"
          title="Paths"
          icon="signs-post"
          to={AppRoutes.pathways}
          state={{ className: "pathways" }}
          badge={careerPlans?.length > 0 && <Badge>{careerPlans.length}</Badge>}
        />
        <NavbarItem
          className="favorites-navbar-item"
          title="Favs"
          icon="stars"
          to={AppRoutes.favorites}
          state={{ className: "favorites" }}
          badge={favorites?.length > 0 && <Badge>{favorites.length}</Badge>}
        />
        <ProfileItemNavbar
          title="Profile"
          position="bottom"
          to={AppRoutes.profile}
          state={{ className: "profile" }}
          user={user}
        />
      </Navbar>
      <div className="outlet">
        <ErrorBoundary
          FallbackComponent={ErrorPage}
          onError={(error, info) => reportReactErrorBoundary({ error, info })}
        >
          <Outlet />
        </ErrorBoundary>
      </div>
    </div>
  );
};
